import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Loader2, ShieldAlert, ShieldCheck, Check, X } from "lucide-react";
import {
  checkAdmin,
  listPaymentRequests,
  reviewPaymentRequest,
  type AdminPaymentRow,
} from "@/lib/admin.functions";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { seo } from "@/lib/seo";
import { toast } from "@/lib/toast";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/admin/payments")({
  head: () => ({
    ...seo({
      title: "Payment Verification — The Quantum Minds Admin",
      description: "Internal admin console for reviewing manual UPI payment requests.",
      noindex: true,
    }),
  }),
  component: AdminPayments,
  errorComponent: ({ error }: { error: unknown }) => (
    <div role="alert" className="p-6 text-sm text-destructive">
      {error instanceof Error ? error.message : String(error)}
    </div>
  ),
});

const STATUSES = ["pending", "approved", "rejected", "all"] as const;
type StatusFilter = (typeof STATUSES)[number];

function rupees(paise: number): string {
  return `₹${(paise / 100).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
}

function when(value: string | null): string {
  if (!value) return "—";
  return new Date(value).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
}

function AdminPayments() {
  const isAdminFn = useServerFn(checkAdmin);
  const listFn = useServerFn(listPaymentRequests);
  const reviewFn = useServerFn(reviewPaymentRequest);
  const queryClient = useQueryClient();

  const [status, setStatus] = useState<StatusFilter>("pending");
  const [pendingAction, setPendingAction] = useState<{
    row: AdminPaymentRow;
    decision: "approve" | "reject";
  } | null>(null);

  const adminQuery = useQuery({
    queryKey: ["admin-check"],
    queryFn: () => isAdminFn({}),
    retry: false,
    staleTime: 60_000,
  });
  const isAdmin = adminQuery.data?.isAdmin === true;

  const listQuery = useQuery({
    queryKey: ["admin-payments", status],
    queryFn: () => listFn({ data: { status } }),
    enabled: isAdmin,
    retry: false,
  });

  const review = useMutation({
    mutationFn: (input: { id: string; decision: "approve" | "reject" }) =>
      reviewFn({ data: { payment_request_id: input.id, decision: input.decision } }),
    onSuccess: (_result, input) => {
      toast.success(input.decision === "approve" ? "Payment approved" : "Payment rejected");
      queryClient.invalidateQueries({ queryKey: ["admin-payments"] });
    },
    onError: (error: Error) => toast.error(error.message.replace(/^Error:\s*/, "")),
    onSettled: () => setPendingAction(null),
  });

  const rows = useMemo(() => listQuery.data ?? [], [listQuery.data]);

  if (adminQuery.isLoading) {
    return (
      <div className="flex min-h-[50vh] items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="mx-auto flex min-h-[50vh] w-full max-w-md flex-col items-center justify-center gap-3 p-6 text-center">
        <ShieldAlert className="h-8 w-8 text-destructive" />
        <h1 className="text-lg font-semibold text-foreground">Admins only</h1>
        <p className="text-sm text-muted-foreground">
          This console is restricted. Every request is verified on the server.
        </p>
      </div>
    );
  }

  return (
    <div className="mx-auto w-full max-w-5xl space-y-5 p-4 pb-24">
      <header className="space-y-1">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-semibold text-foreground">Payment verification</h1>
        </div>
        <p className="text-sm text-muted-foreground">
          Approve or reject manual UPI payments. Amounts are re-verified by the server.
        </p>
      </header>

      <div className="flex flex-wrap gap-2">
        {STATUSES.map((s) => (
          <button
            key={s}
            onClick={() => setStatus(s)}
            className={cn(
              "rounded-full border px-4 py-1.5 text-xs font-medium capitalize transition",
              status === s
                ? "border-primary/60 bg-primary/15 text-primary"
                : "border-border/60 bg-card/50 text-muted-foreground hover:text-foreground",
            )}
          >
            {s}
          </button>
        ))}
      </div>

      {listQuery.isLoading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        </div>
      ) : listQuery.error ? (
        <p className="text-sm text-destructive">{(listQuery.error as Error).message}</p>
      ) : rows.length === 0 ? (
        <p className="rounded-2xl border border-border/60 bg-card/50 p-6 text-center text-sm text-muted-foreground">
          No {status === "all" ? "" : status} payment requests.
        </p>
      ) : (
        <ul className="space-y-3">
          {rows.map((row) => (
            <li
              key={row.id}
              className="rounded-2xl border border-border/60 bg-card/60 p-4 backdrop-blur"
            >
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-foreground">
                    {row.userEmail ?? "—"}
                  </p>
                  <p className="truncate font-mono text-[11px] text-muted-foreground">
                    req {row.id}
                  </p>
                  <p className="truncate font-mono text-[11px] text-muted-foreground">
                    user {row.userId}
                  </p>
                </div>
                <span
                  className={cn(
                    "rounded-full px-3 py-1 text-[11px] font-semibold capitalize",
                    row.status === "pending" && "bg-amber-500/15 text-amber-500",
                    row.status === "approved" && "bg-emerald-500/15 text-emerald-500",
                    row.status === "rejected" && "bg-destructive/15 text-destructive",
                    row.status === "cancelled" && "bg-muted text-muted-foreground",
                  )}
                >
                  {row.status}
                </span>
              </div>

              <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1.5 text-xs sm:grid-cols-3">
                <Field label="Plan" value={`${row.planName} (${row.planId})`} />
                <Field label="Type" value={row.paymentType.replace(/_/g, " ")} />
                <Field label="Base" value={rupees(row.baseAmountPaise)} />
                <Field
                  label="Add-on"
                  value={
                    row.simulationAddonSelected
                      ? `Yes · ${rupees(row.simulationAddonAmountPaise)}`
                      : "No"
                  }
                />
                <Field
                  label="Final"
                  value={rupees(row.finalAmountPaise)}
                  warn={!row.amountMatches}
                />
                <Field label="Expected" value={rupees(row.expectedFinalAmountPaise)} />
                <Field label="UTR" value={row.utr ?? "not submitted"} mono />
                <Field label="Payer" value={row.payerName ?? "—"} />
                <Field label="Submitted" value={when(row.createdAt)} />
              </dl>

              {!row.amountMatches ? (
                <p className="mt-2 text-xs text-destructive">
                  Amount mismatch with current plan pricing — approval will be blocked.
                </p>
              ) : null}

              {row.status === "pending" ? (
                <div className="mt-4 flex gap-2">
                  <button
                    disabled={review.isPending}
                    onClick={() => setPendingAction({ row, decision: "approve" })}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-emerald-500/15 px-4 py-2 text-sm font-medium text-emerald-500 transition hover:bg-emerald-500/25 disabled:opacity-50"
                  >
                    <Check className="h-4 w-4" /> Approve
                  </button>
                  <button
                    disabled={review.isPending}
                    onClick={() => setPendingAction({ row, decision: "reject" })}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-xl bg-destructive/15 px-4 py-2 text-sm font-medium text-destructive transition hover:bg-destructive/25 disabled:opacity-50"
                  >
                    <X className="h-4 w-4" /> Reject
                  </button>
                </div>
              ) : (
                <p className="mt-3 text-xs text-muted-foreground">
                  Reviewed {when(row.reviewedAt)}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      <AlertDialog
        open={pendingAction !== null}
        onOpenChange={(open) => !open && setPendingAction(null)}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {pendingAction?.decision === "approve" ? "Approve payment?" : "Reject payment?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {pendingAction
                ? pendingAction.decision === "approve"
                  ? `This activates ${pendingAction.row.planName} for ${pendingAction.row.userEmail ?? pendingAction.row.userId} and cannot be undone here.`
                  : `This marks the request from ${pendingAction.row.userEmail ?? pendingAction.row.userId} as rejected. No access is granted.`
                : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={review.isPending}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              disabled={review.isPending}
              onClick={(event) => {
                event.preventDefault();
                if (!pendingAction) return;
                review.mutate({ id: pendingAction.row.id, decision: pendingAction.decision });
              }}
            >
              {review.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Field({
  label,
  value,
  mono,
  warn,
}: {
  label: string;
  value: string;
  mono?: boolean;
  warn?: boolean;
}) {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd
        className={cn(
          "truncate text-foreground",
          mono && "font-mono text-[11px]",
          warn && "text-destructive",
        )}
      >
        {value}
      </dd>
    </div>
  );
}
